/** Recorded Salamander Grand Piano samples (CC BY 3.0). */
const SAMPLE_MIDI: Record<string, number> = {
  A0: 21, A1: 33, A2: 45, A3: 57, A4: 69, A5: 81, A6: 93, A7: 105,
  C1: 24, C2: 36, C3: 48, C4: 60, C5: 72, C6: 84, C7: 96, C8: 108,
  Ds1: 27, Ds2: 39, Ds3: 51, Ds4: 63, Ds5: 75, Ds6: 87, Ds7: 99,
  Fs1: 30, Fs2: 42, Fs3: 54, Fs4: 66, Fs5: 78, Fs6: 90, Fs7: 102,
}

const nearestSample = (midi: number) => Object.entries(SAMPLE_MIDI).reduce((best, entry) => Math.abs(entry[1] - midi) < Math.abs(best[1] - midi) ? entry : best)

export class PianoAudio {
  private context?: AudioContext
  private buffers = new Map<string, Promise<AudioBuffer>>()
  private warmed = false

  constructor(private getVolume: () => number, private isMuted: () => boolean) {}

  private getContext() { return this.context ??= new AudioContext() }

  private load(name: string) {
    const cached = this.buffers.get(name)
    if (cached) return cached
    const load = fetch(`/audio/${name}.mp3`)
      .then((response) => { if (!response.ok) throw new Error(`Не найден сэмпл ${name}`); return response.arrayBuffer() })
      .then((data) => this.getContext().decodeAudioData(data))
    this.buffers.set(name, load)
    return load
  }

  play(midi: number) {
    if (this.isMuted()) return
    const context = this.getContext()
    if (context.state === 'suspended') void context.resume()
    const [name, sourceMidi] = nearestSample(midi)
    void this.playSample(name, sourceMidi, midi)
    if (!this.warmed) {
      this.warmed = true
      void Promise.all(Object.keys(SAMPLE_MIDI).map((sample) => this.load(sample))).catch(() => undefined)
    }
  }

  private async playSample(name: string, sourceMidi: number, midi: number) {
    try {
      const context = this.getContext()
      const source = context.createBufferSource()
      source.buffer = await this.load(name)
      source.playbackRate.value = 2 ** ((midi - sourceMidi) / 12)
      const gain = context.createGain()
      const now = context.currentTime
      const level = Math.max(0.0001, Math.min(1, this.getVolume() * 0.55))
      gain.gain.setValueAtTime(level, now)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 3.2)
      const filter = context.createBiquadFilter()
      filter.type = 'highpass'
      filter.frequency.value = 18
      source.connect(filter).connect(gain).connect(context.destination)
      source.start(now)
      source.stop(now + 3.25)
    } catch {
      // The next key press can retry if a temporary network/audio error occurred.
    }
  }
}
